import { useBreakpoint } from '@insify/react-hooks';
import { animated, easings, useTransition } from '@react-spring/web';

import styles from './PageTransition.module.css';
import { usePageTransition } from './PageTransitionProvider';

import type { UseTransitionProps } from '@react-spring/web';
import type React from 'react';

export type TransitionDirection = 'forward' | 'backward' | 'off';

type PageTransitionProps<LocationObj = unknown> = {
  children: (_: LocationObj) => React.ReactNode;
  location: LocationObj;
  className?: string;
  duration?: number;
  offset?: number;
  direction?: TransitionDirection;
};

const createTransitionConfig = ({
  duration,
  offset,
  direction,
  setDirection,
}: Pick<PageTransitionProps, 'duration' | 'offset' | 'direction'> & {
  setDirection: React.Dispatch<React.SetStateAction<TransitionDirection>>;
}): UseTransitionProps => {
  const config: UseTransitionProps = {
    config: {
      duration,
      easing: easings.easeInOutQuart,
    },
    immediate: direction === 'off',
    initial: { position: 'static', transform: 'translateX(0)', opacity: 1 },
  };

  switch (direction) {
    case 'forward':
      return {
        ...config,
        from: {
          position: 'absolute',
          transform: `translateX(${offset}px)`,
          opacity: 0,
        },
        enter: {
          position: 'static',
          transform: `translateX(0px)`,
          opacity: 1,
        },
        leave: {
          position: 'absolute',
          transform: `translateX(-${offset}px)`,
          opacity: 0,
          top: -1 * scrollY,
          // @ts-ignore - TS doesn't know about behavior instant but it's an legit value https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollTo
          onStart: () => scrollTo({ top: 0, behavior: 'instant' }),
          onRest: () => setDirection('off'),
        },
      };
    case 'backward':
      return {
        ...config,
        from: {
          position: 'absolute',
          transform: `translateX(-${offset}px)`,
          opacity: 0,
        },
        enter: {
          position: 'static',
          transform: `translateX(0px)`,
          opacity: 1,
        },
        leave: {
          position: 'absolute',
          transform: `translateX(${offset}px)`,
          opacity: 0,
          top: -1 * scrollY,
          // @ts-ignore - TS doesn't know about behavior instant but it's an legit value https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollTo
          onStart: () => scrollTo({ top: 0, behavior: 'instant' }),
          onRest: () => setDirection('off'),
        },
      };
    case 'off':
      return {
        ...config,
        from: { position: 'static', transform: `translateX(0px)`, opacity: 1 },
        enter: { position: 'static', transform: `translateX(0px)`, opacity: 1 },
        leave: { position: 'static', transform: `translateX(0px)`, opacity: 1 },
      };
    default:
      return config;
  }
};

export const PageTransition = <LocationObj extends { pathname: string }>({
  location,
  duration = 250,
  children,
}: PageTransitionProps<LocationObj>): JSX.Element => {
  const { direction, setDirection } = usePageTransition();
  const isDesktop = useBreakpoint('(min-width: 960px)');
  const offset = window.innerWidth - (isDesktop ? 320 : 0);
  const transitions = useTransition(location, {
    ...createTransitionConfig({ duration, offset, direction, setDirection }),
  });

  return (
    <div className={styles.container}>
      {transitions((style, location) => (
        <animated.div className={styles.animatedWrapper} style={style}>
          {children(location)}
        </animated.div>
      ))}
    </div>
  );
};
